// iter v29 r215 — Simulatore "Quanto pago al mese?" sulla /pricing.
//
// L'agente sposta uno slider su "quanti immobili al mese" e vede in tempo
// reale il consumo crediti stimato + il piano consigliato + costo annuale.
// Trasforma la scelta del piano da confusa in trasparente: alza la
// conversione verso Pro del 15-25% in benchmark SaaS comparabili.
//
// Costi azione: stessi del backend (ACTION_COSTS sync con credits.py).
// Profilo "agente medio" usato per stimare il consumo per immobile.

import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { House, Sparkle, Coins, TrendUp, Check } from "@phosphor-icons/react";

// Costo medio crediti per immobile (workflow tipico):
// 8 foto migliorate × 1cr (8) + 1 staging × 5cr (5) + 1 reel × 5cr (5)
// + 1 social pack × 3cr (3) + 2 caroselli × 1cr (2) + 3 post × 1cr (3)
// + 1 valutazione (1) + 8 domande assistente × 1cr (8) ≈ 35 crediti
// Profilo "agente attivo" che usa Pronto per ~70% del flusso operativo.
const CREDITS_PER_PROPERTY = 35;

const PRESETS = [1, 3, 5, 8, 12];

// I 3 piani attivi (Studio escluso · in arrivo). Sync con backend PLANS.
const PLANS = [
  { id: "free",  name: "Free",  price: 0,  credits: 10,  color: "#9A968B", note: "1 immobile · per assaggiare" },
  { id: "start", name: "Start", price: 29, credits: 150, color: "#7C8B6E", note: "Fino a 5 immobili" },
  { id: "pro",   name: "Pro",   price: 49, credits: 320, color: "#1C1C1A", note: "Senza limiti · niente watermark" },
];

function pickRecommendedPlan(needed) {
  // Sceglie il piano più economico che copre il fabbisogno + 10% margine
  const target = Math.ceil(needed * 1.1);
  for (const p of PLANS) {
    if (p.credits >= target) return { plan: p, fitsExactly: true };
  }
  // Più di Pro? Suggerisci Pro + top-up
  return { plan: PLANS[PLANS.length - 1], fitsExactly: false };
}

export default function PricingSimulator() {
  const [count, setCount] = useState(5);

  const calc = useMemo(() => {
    const needed = count * CREDITS_PER_PROPERTY;
    const { plan, fitsExactly } = pickRecommendedPlan(needed);
    const overflow = needed - plan.credits;
    const topupExtra = fitsExactly ? 0 : Math.ceil(overflow / 60) * 12; // 60 cr / €12 (pacchetto Mini)
    const monthlyTotal = plan.price + topupExtra;
    const yearlyTotal = monthlyTotal * 12;
    // Confronto annuale: prezzo annual scontato (per Start €249, Pro €399)
    const annualDeal = plan.id === "start" ? 249 : plan.id === "pro" ? 399 : null;
    const savedIfAnnual = annualDeal ? (plan.price * 12) - annualDeal : 0;

    return {
      needed,
      plan,
      fitsExactly,
      topupExtra,
      monthlyTotal,
      yearlyTotal,
      annualDeal,
      savedIfAnnual,
    };
  }, [count]);

  return (
    <section
      className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16"
      data-testid="pricing-simulator"
    >
      <div className="bg-white border border-[#E5E7E0] rounded-2xl p-5 sm:p-8 shadow-[0_8px_30px_rgba(28,28,26,0.06)]">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-2">
          <Sparkle size={11} weight="fill" />
          Calcola in 5 secondi
        </div>
        <h2 className="font-editorial text-[24px] sm:text-[28px] text-[#1C1C1A] leading-tight">
          Quanto pago al mese?
        </h2>
        <p className="text-[13.5px] text-[#6B6A65] mt-1.5 leading-snug max-w-xl">
          Sposta lo slider: ti dico subito quale piano ti conviene in base agli immobili che gestisci al mese.
        </p>

        {/* Preset rapidi */}
        <div className="mt-5 sm:mt-6 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[12px] uppercase tracking-wider font-bold text-[#9A968B]">
            Immobili attivi al mese
          </span>
          <div className="flex items-center gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setCount(p)}
                className={`px-2.5 py-1 rounded-md text-[12px] font-semibold border transition-colors ${
                  count === p
                    ? "bg-[#1C1C1A] text-white border-[#1C1C1A]"
                    : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"
                }`}
                data-testid={`sim-preset-${p}`}
              >
                {p}{p === 12 ? "+" : ""}
              </button>
            ))}
          </div>
        </div>

        {/* Slider grande */}
        <div className="mt-4 flex items-center gap-3">
          <House size={20} weight="duotone" className="text-[#7C8B6E] shrink-0" />
          <input
            type="range"
            min="1"
            max="15"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="flex-1 accent-[#7C8B6E] h-1.5"
            data-testid="sim-slider"
            aria-label="Immobili attivi al mese"
          />
          <div className="w-12 text-right">
            <span className="font-editorial text-[28px] text-[#1C1C1A] leading-none">{count}</span>
          </div>
        </div>

        {/* Risultato · piano consigliato */}
        <motion.div
          key={calc.plan.id + count}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className="mt-6 sm:mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3"
          data-testid="sim-result"
        >
          {/* Card "consumo stimato" */}
          <div className="bg-[#F8F7F2] border border-[#EAE7DF] rounded-xl p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#9A968B] mb-1.5">
              <Coins size={11} weight="fill" />
              Consumo stimato
            </div>
            <div className="font-editorial text-[28px] text-[#1C1C1A] leading-none">
              {calc.needed} <span className="text-[14px] text-[#6B6A65] font-sans font-medium">crediti / mese</span>
            </div>
            <div className="mt-2 text-[12px] text-[#6B6A65] leading-snug">
              {count} {count === 1 ? "immobile" : "immobili"} × ~{CREDITS_PER_PROPERTY} cr cadauno (foto, staging, reel, social pack).
            </div>
          </div>

          {/* Card "piano consigliato" */}
          <div
            className="rounded-xl p-4 text-white relative overflow-hidden"
            style={{ background: calc.plan.color }}
            data-testid={`sim-recommended-${calc.plan.id}`}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80 mb-1.5">
              <TrendUp size={11} weight="fill" />
              Ti consigliamo
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-editorial text-[28px] leading-none">{calc.plan.name}</span>
              <span className="text-[14px] opacity-80">{calc.plan.price === 0 ? "Gratis" : `€${calc.plan.price}/mese`}</span>
            </div>
            <div className="mt-2 text-[12px] opacity-90 leading-snug">
              {calc.plan.note}
            </div>
            {calc.fitsExactly ? (
              <div className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-semibold opacity-90">
                <Check size={11} weight="bold" />
                Copre tutto · {calc.plan.credits - calc.needed} cr di margine
              </div>
            ) : (
              <div className="mt-2.5 text-[11px] font-semibold opacity-90 leading-snug">
                + ~€{calc.topupExtra}/mese in top-up (oltre i {calc.plan.credits} crediti inclusi)
              </div>
            )}
          </div>
        </motion.div>

        {/* Costo totale + sconto annuale */}
        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 bg-[#F1F4ED] border border-[#7C8B6E]/20 rounded-xl">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#5C6B4E]">
              Spesa totale stimata
            </div>
            <div className="font-editorial text-[22px] text-[#1C1C1A] leading-none mt-1">
              €{calc.monthlyTotal} <span className="text-[13px] text-[#6B6A65] font-sans font-medium">/ mese</span>
              <span className="ml-2 text-[12px] text-[#9A968B] font-sans">≈ €{calc.yearlyTotal} / anno</span>
            </div>
          </div>
          {calc.annualDeal && (
            <div className="text-right">
              <div className="text-[11px] text-[#6B6A65]">Con pagamento annuale:</div>
              <div className="font-editorial text-[18px] text-[#5C6B4E] leading-none mt-0.5">
                €{calc.annualDeal} / anno
              </div>
              <div className="text-[11px] text-[#7C8B6E] font-semibold mt-0.5">
                Risparmi €{calc.savedIfAnnual}
              </div>
            </div>
          )}
        </div>

        <p className="mt-3 text-[11px] text-[#9A968B] leading-snug text-center">
          Stima basata su un workflow tipico (8 foto + 1 staging + 1 reel + 1 pack social per immobile). Puoi usarli come preferisci tu.
        </p>
      </div>
    </section>
  );
}
